import { useState, useEffect, useCallback } from 'preact/hooks';

export type Region = 'au' | 'us' | 'intl';

const STORAGE_KEY = 'giraffe_region';
const DEFAULT_REGION: Region = 'intl';
const REGION_CHANGE_EVENT = 'giraffe:region-change';

function inferRegionFromTimeZone(): Region {
  if (typeof Intl === 'undefined' || typeof Intl.DateTimeFormat === 'undefined') {
    return DEFAULT_REGION;
  }
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    if (tz.startsWith('Australia/')) return 'au';
    if (tz.startsWith('US/') || tz.startsWith('America/')) return 'us';
  } catch {
    // ignore
  }
  return DEFAULT_REGION;
}

// Simple geo IP lookup
export async function detectRegionFromIP(): Promise<Region> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 800);
    
    const response = await fetch('https://ipapi.co/json/', {
      signal: controller.signal,
    });
    
    clearTimeout(timeoutId);
    
    if (response.ok) {
      const data = await response.json();
      const countryCode = data.country_code as string;
      
      if (countryCode === 'AU') return 'au';
      if (countryCode === 'US') return 'us';
    }

    // Fall back to timezone-based inference if the API fails or returns other countries
    return inferRegionFromTimeZone();
  } catch {
    return inferRegionFromTimeZone();
  }
}

export function getRegionFromQuery(): Region | null {
  if (typeof window === 'undefined') return null;
  
  const params = new URLSearchParams(window.location.search);
  const region = params.get('region') as Region;
  
  if (region === 'au' || region === 'us' || region === 'intl') {
    return region;
  }
  
  return null;
}

function getRegionFromStorage(): Region | null {
  if (typeof window === 'undefined') return null;
  
  try {
    const stored = localStorage.getItem(STORAGE_KEY) as Region;
    if (stored === 'au' || stored === 'us' || stored === 'intl') {
      return stored;
    }
  } catch {
    // localStorage not available
  }
  
  return null;
}

function saveRegionToStorage(region: Region): void {
  if (typeof window === 'undefined') return;
  
  try {
    localStorage.setItem(STORAGE_KEY, region);
  } catch {
    // localStorage not available
  }
}

export function useRegion() {
  const [region, setRegionState] = useState<Region>(DEFAULT_REGION);
  const [isDetecting, setIsDetecting] = useState(true);

  const setRegion = useCallback((newRegion: Region) => {
    setRegionState(newRegion);
    saveRegionToStorage(newRegion);

    // Update URL query param without navigation
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.set('region', newRegion);
      window.history.replaceState({}, '', url.toString());
      // Notify all other useRegion instances
      window.dispatchEvent(new CustomEvent(REGION_CHANGE_EVENT, { detail: newRegion }));
    }
  }, []);

  useEffect(() => {
    // Check query param first (highest priority)
    const queryRegion = getRegionFromQuery();
    if (queryRegion) {
      setRegionState(queryRegion);
      saveRegionToStorage(queryRegion);
      setIsDetecting(false);
      return;
    }
    
    // Check localStorage
    const storedRegion = getRegionFromStorage();
    if (storedRegion) {
      setRegionState(storedRegion);
      setIsDetecting(false);
      return;
    }
    
    // Detect from IP (async)
    detectRegionFromIP().then((detectedRegion) => {
      setRegionState(detectedRegion);
      saveRegionToStorage(detectedRegion);
      setIsDetecting(false);
    });
  }, []);

  // Listen for query param changes
  useEffect(() => {
    const handlePopState = () => {
      const queryRegion = getRegionFromQuery();
      if (queryRegion && queryRegion !== region) {
        setRegionState(queryRegion);
        saveRegionToStorage(queryRegion);
      }
    };

    // Listen for region changes from other hook instances
    const handleRegionChange = (e: Event) => {
      const newRegion = (e as CustomEvent<Region>).detail;
      if (newRegion && newRegion !== region) {
        setRegionState(newRegion);
      }
    };

    window.addEventListener('popstate', handlePopState);
    window.addEventListener(REGION_CHANGE_EVENT, handleRegionChange);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener(REGION_CHANGE_EVENT, handleRegionChange);
    };
  }, [region]);

  return { region, setRegion, isDetecting };
}
